import { initI18n } from '@/lib/i18n';

/**
 * The `scheduling` and `messaging` i18n namespaces (BP-04 §6).
 *
 * Registered from the feature rather than added to `src/lib/i18n/resources.ts`, for the
 * reason `features/inventory/strings.ts` gives: a feature package owns its copy, and the
 * shared bundle stays the chrome's.
 *
 * **No key here interpolates a client's name or number.** The calendar labels a booking by
 * its `ref` and the message log by the api's mask (§9); a screen that wanted the real value
 * would have to fetch it from a route that requires `client.read_full`.
 */
export const SCHEDULING_NAMESPACE = 'scheduling';
export const MESSAGING_NAMESPACE = 'messaging';

export const schedulingStrings = {
  nav: {
    calendar: 'Calendar',
    staff: 'Staff',
    scheduling: 'Scheduling',
    messaging: 'Messaging',
  },
  calendar: {
    title: 'Calendar',
    none: 'No bookings today',
    previous: 'Previous day',
    next: 'Next day',
    today: 'Today',
    newBooking: 'New booking',
    walkIn: 'Walk-in',
    offline: 'Offline — showing the last loaded day, read-only',
    conflict: 'That slot has just been taken. The day has been reloaded.',
    retry: 'Try again',
    noStaff: 'No bookable staff on the roster',
    ofStaff: '{{name}}',
    search: 'Search the day',
    searchPlaceholder: 'Reference, service…',
    showCancelled: 'Show cancelled',
    whoseColumn: 'Whose column',
    freeNow: 'Free now',
    loading: 'Loading the day…',
    emptyTitle: 'Nothing booked yet',
    emptyBody: 'Take a walk-in, or open a booking from the phone.',
    gap: {
      unscheduled: 'Not scheduled',
      lunch: 'Lunch',
    },
  },
  card: {
    label: '{{ref}} · {{time}}',
    status: 'Status',
  },
  status: {
    requested: 'Requested',
    confirmed: 'Confirmed',
    checked_in: 'Checked in',
    in_service: 'In service',
    completed: 'Completed',
    no_show: 'No show',
    cancelled: 'Cancelled',
  },
  source: {
    walkin: 'Walk-in',
    web: 'Web',
    app: 'App',
    staff: 'Staff',
    whatsapp: 'WhatsApp',
    ar: 'AR',
    google: 'Google',
  },
  drawer: {
    title: 'Booking {{ref}}',
    close: 'Close',
    items: 'Services',
    when: 'When',
    via: 'Booked via',
    with: 'with {{name}}',
    reminders: 'Reminders',
    messages: 'Messages',
    noMessages: 'Nothing sent yet',
    notes: 'Notes',
    reason: 'Reason',
    reasonRequired: 'A reason is required to cancel or mark a no-show.',
    reschedule: 'Reschedule',
    cancelReschedule: 'Keep this booking',
    confirmReschedule: 'Move the booking',
    noTargets: 'No transitions are available from here',
  },
  create: {
    title: 'New booking',
    walkInTitle: 'Walk-in',
    client: 'Client',
    clientHint: 'Leave blank for a booking with no client record',
    services: 'Services',
    staff: 'Staff member',
    anyStaff: 'Any available',
    date: 'Date',
    slots: 'Slots',
    noSlots: 'No free slot on that day',
    walkInNow: 'Starting now, on the first free technician.',
    checkInNow: 'Check in now',
    submit: 'Create the booking',
    cancel: 'Cancel',
    phone: 'Phone',
    name: 'Name',
    pickService: 'Pick at least one service',
    pickSlot: 'Pick a slot',
  },
  staff: {
    title: 'Staff',
    none: 'No staff yet',
    displayName: 'Name',
    skills: 'Skills',
    level: 'Level',
    levels: { trainee: 'Trainee', competent: 'Competent', expert: 'Expert' },
    colour: 'Colour',
    bookable: 'Bookable',
    sortOrder: 'Order',
    active: 'Active',
    add: 'Add a staff member',
    save: 'Save',
    open: 'Open',
    schedule: 'Weekly schedule',
    timeOff: 'Time off',
    weekday: 'Day',
    start: 'From',
    end: 'To',
    validFrom: 'Valid from',
    validTo: 'Valid to',
    addRow: 'Add a row',
    removeRow: 'Remove',
    saveSchedule: 'Save the schedule',
    noTimeOff: 'No time off booked',
    addTimeOff: 'Add time off',
    deleteTimeOff: 'Delete',
    timeOffReason: 'Reason',
    back: 'Back to the roster',
  },
  weekday: {
    0: 'Monday',
    1: 'Tuesday',
    2: 'Wednesday',
    3: 'Thursday',
    4: 'Friday',
    5: 'Saturday',
    6: 'Sunday',
  },
  settings: {
    title: 'Scheduling',
    autoConfirm: 'Confirm online bookings automatically',
    bufferMin: 'Buffer after each service (minutes)',
    slotStepMin: 'Slot step (minutes)',
    clientCutoffHours: 'Client cut-off before the start (hours)',
    clientMaxReschedules: 'Reschedules a client may make',
    minLeadMin: 'Minimum lead time (minutes)',
    maxAdvanceDays: 'How far ahead a client may book (days)',
    noShowGraceMin: 'No-show grace (minutes)',
    noShowUndoMin: 'No-show undo window (minutes)',
    save: 'Save',
    saved: 'Saved',
    readOnly: 'The tenant is read-only; these settings cannot be changed.',
  },
} as const;

export const messagingStrings = {
  settings: {
    title: 'Messaging',
    relay: 'Sending number',
    mode: 'Mode',
    modeOwnNumber: 'The salon’s own WhatsApp number',
    modeRelay: '26AD’s shared number',
    phoneNumberId: 'Meta phone number id',
    wabaId: 'WhatsApp Business Account id',
    senderDisplay: 'Sender shown to the client',
    verifyTokenRef: 'Webhook verify-token secret',
    saveRelay: 'Save the sending number',
    whatsappDisabled:
      'WhatsApp is switched off until Meta verifies the number; messages are logged, not sent.',
    templates: 'Templates',
    templateKey: 'Key',
    templateChannel: 'Channel',
    templateCategory: 'Category',
    templateStatus: 'Meta status',
    templateActive: 'Active',
    noTemplates: 'No templates yet',
    rateCard: 'Rate card',
    rateVersion: 'Version',
    utility: 'Utility (₹)',
    authentication: 'Authentication (₹)',
    marketing: 'Marketing (₹)',
    service: 'Service (₹)',
    quietHours: 'Quiet hours',
    quietFrom: 'From',
    quietTo: 'To',
    // BP-18 §6. The copy says what the salon has to have done before any
    // of it works: DLT registration is paperwork with the operators, and
    // a screen that implied otherwise would let an owner turn SMS on and
    // discover at midnight that every message was scrubbed.
    sms: 'SMS (DLT)',
    smsHelp:
      'SMS in India needs a DLT registration with the operators — an entity id, a sender id and every message body approved in advance. Fill this in after that is done.',
    smsEnabled: 'Send SMS',
    smsEntity: 'DLT entity id',
    smsHeaders: 'Sender ids',
    smsHeadersHelp:
      'The six-character headers you registered, separated by commas. Each template says which one it goes out under.',
    smsUrlHosts: 'Allowed link hosts',
    smsUrlHostsHelp:
      'Only links on these hosts may appear in an SMS — the operator drops the rest. Separated by commas.',
    smsFallback: 'Send an SMS when WhatsApp fails',
    smsFallbackHelp:
      'Two messages, and you pay for both. Off until your DLT templates are approved, or every failed reminder becomes an SMS that cannot be delivered either.',
    smtp: 'Email (SMTP)',
    smtpHost: 'Host',
    smtpPort: 'Port',
    smtpUsername: 'Username',
    smtpFromEmail: 'From address',
    smtpFromName: 'From name',
    smtpStarttls: 'STARTTLS',
    save: 'Save',
    saved: 'Saved',
    // BP-11 R7. The copy says what it costs and what it needs, because an
    // owner turning it on cannot see either from the switch: push is free
    // and WhatsApp is not, and a client with no app installed gets nothing
    // from it at all.
    pushChannel: 'Also send reminders to the 26AD app',
    pushChannelHelp:
      'Free, and only reaches clients who have installed the app and allowed notifications. WhatsApp still goes out either way.',
    testPush: 'Send a test push',
    testPushClient: 'Client id',
    testPushSent: 'Sent to every phone that client has registered.',
    testSend: 'Send a test',
    testTo: 'To (E.164)',
    testTemplate: 'Template',
    testSent: 'Queued.',
  },
  log: {
    title: 'Messages',
    none: 'Nothing sent yet',
    to: 'To',
    template: 'Template',
    status: 'Status',
    cost: 'Cost',
    sentAt: 'Sent',
    more: 'Load more',
  },
} as const;

/** Add both bundles to the shared i18next instance. Idempotent. */
export function registerSchedulingStrings(): void {
  const i18n = initI18n();
  i18n.addResourceBundle('en', SCHEDULING_NAMESPACE, schedulingStrings, true, false);
  i18n.addResourceBundle('en', MESSAGING_NAMESPACE, messagingStrings, true, false);
}

registerSchedulingStrings();
